import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { PrismaComputeInput } from '@/features/export/prisma-diagram';

interface PrismaRPCResult {
  total: number;
  duplicates: number;
  screening_excluded: number;
  screening_ai_excluded: number;
  screening_human_excluded: number;
  fulltext_excluded: number;
  fulltext_ai_excluded: number;
  fulltext_human_excluded: number;
  included: number;
  exclusion_reasons: Array<{ reason: string; count: number }>;
}

/**
 * Fetch PRISMA flow data from a review via the get_prisma_data RPC.
 * Maps the result to PrismaComputeInput for the diagram generator.
 */
export function usePrismaData(reviewId: string | undefined) {
  return useQuery({
    queryKey: ['prisma-data', reviewId],
    queryFn: async (): Promise<PrismaComputeInput & {
      aiExcluded?: number;
      humanExcluded?: number;
    }> => {
      if (!reviewId) {
        throw new Error('Review ID required');
      }

      const { data, error } = await supabase.rpc('get_prisma_data', {
        p_review_id: reviewId,
      });

      if (error) throw error;

      const result = data as PrismaRPCResult;

      return {
        totalIdentifiedDatabases: result.total,
        totalIdentifiedOther: 0,
        duplicatesRemoved: result.duplicates,
        automationRemoved: 0,
        otherRemoved: 0,
        screeningExcluded: result.screening_excluded,
        retrievalFailed: 0,
        eligibilityExcluded: result.fulltext_excluded,
        exclusionReasons: result.exclusion_reasons ?? [],
        includedStudies: result.included,
        aiExcluded: (result.screening_ai_excluded ?? 0) + (result.fulltext_ai_excluded ?? 0),
        humanExcluded: (result.screening_human_excluded ?? 0) + (result.fulltext_human_excluded ?? 0),
      };
    },
    enabled: !!reviewId,
  });
}
